/**
 * Listing free-text policy — title, description, and defects.
 * Single source of truth for client + server validation.
 */

export type ListingFreeTextViolationCode =
  | "phone"
  | "email"
  | "url"
  | "off_platform_contact"
  | "negotiation"
  | "address";

export type ListingFreeTextFieldKind = "title" | "description" | "defects";

export interface ListingFreeTextViolation {
  code: ListingFreeTextViolationCode;
  message: string;
}

export interface ListingFreeTextViolationWithField
  extends ListingFreeTextViolation {
  field: ListingFreeTextFieldKind;
}

/** Who is reading the free-text error — listing sell vs buyer request notes. */
export type FreeTextMessageAudience = "listing" | "request";

const LISTING_VIOLATION_MESSAGES: Record<ListingFreeTextViolationCode, string> =
  {
    phone:
      "Remove phone numbers from this box. Buyers contact you through Sell It Off after approval.",
    email:
      "Remove email addresses from this box. Buyers contact you through Sell It Off.",
    url: "Remove links and social handles. They are not allowed in this box.",
    off_platform_contact:
      "Remove WhatsApp, DM me, or call-me requests from this box. Buyers reach you through Sell It Off.",
    negotiation:
      "Remove negotiation wording such as negotiable or open to offers. List the price you want buyers to see.",
    address:
      "Remove street addresses or house numbers. Pickup area is set separately in Location.",
  };

/** Shared contact codes — plain language for buyer request Extra details. */
const REQUEST_NOTES_VIOLATION_MESSAGES: Record<
  Extract<
    ListingFreeTextViolationCode,
    "phone" | "email" | "url" | "off_platform_contact"
  >,
  string
> = {
  phone: "Remove phone numbers — use this box for item details only.",
  email: "Remove email addresses — use this box for item details only.",
  url: "Remove links — use this box for item details only.",
  off_platform_contact:
    "Don’t add WhatsApp, “DM me,” or call-me here — just extra details about the item.",
};

export function freeTextViolationMessage(
  code: ListingFreeTextViolationCode,
  audience: FreeTextMessageAudience = "listing",
): string {
  if (audience === "request") {
    if (
      code === "phone" ||
      code === "email" ||
      code === "url" ||
      code === "off_platform_contact"
    ) {
      return REQUEST_NOTES_VIOLATION_MESSAGES[code];
    }
  }
  return LISTING_VIOLATION_MESSAGES[code];
}

/** Placeholders shared by sell, bulk, and edit surfaces. */
export const LISTING_DESCRIPTION_PLACEHOLDER =
  "Condition, size, brand, and features — no phone numbers, addresses, or “negotiable”.";

export const LISTING_DEFECTS_PLACEHOLDER =
  "Honest wear and tear only — not contact details or pickup address.";

export const LISTING_TITLE_POLICY_HINT =
  "No phone numbers, links, or “negotiable” in the title.";

const EMAIL_PATTERN = /[a-z0-9._%+-]+@[a-z0-9.-]+\.[a-z]{2,}/i;

const URL_PATTERN =
  /(?:https?:\/\/|www\.)[^\s]+|(?:wa\.me|bit\.ly|t\.me)\/[^\s]*|\b(?:instagram|facebook|fb|tiktok|twitter|x)\.com\/[^\s]*/i;

const PHONE_CANDIDATE_PATTERN =
  /(?:\+?\s*234[\s.-]?|0)[789][01](?:[\s.-]?\d){8}/gi;

const NEGOTIATION_PATTERNS: RegExp[] = [
  /\b(?:slightly\s+)?negotiable\b/i,
  /\bopen\s+to\s+(?:offers?|negotiation)\b/i,
  /\bbest\s+offer\b/i,
  /\blast\s+price\b/i,
  /\bprice\s+is\s+not\s+fixed\b/i,
  /\bnot\s+fixed\s+price\b/i,
  /\bcall\s+(?:me\s+)?to\s+negotiate\b/i,
  /\bcan\s+negotiate\b/i,
  /\bnegotiate\s+(?:the\s+)?price\b/i,
];

const OFF_PLATFORM_CONTACT_PATTERNS: RegExp[] = [
  /\bwhatsapp\s+me\b/i,
  /\b(?:my|on|via|through)\s+whatsapp\b/i,
  /\bwhats\s*app\b/i,
  /\bwa\.me\b/i,
  /\bdm\s+me\b/i,
  /\breach\s+me\s+(?:on|at|via|directly)\b/i,
  /\bcontact\s+me\s+(?:on|at|directly|via)\b/i,
  /\bmessage\s+me\s+(?:on|at|via)\b/i,
  /\bchat\s+me\s+(?:on|up)?\b/i,
  /\badd\s+me\s+on\b/i,
  /\bcall\s+me\s+(?:on|at|direct)\b/i,
];

/** Conservative street-address patterns — not bare area/LGA names. */
const ADDRESS_PATTERNS: RegExp[] = [
  /\b(?:No\.?|Num\.?|Number)\s*\d+[a-z]?\b/i,
  /\bPlot\s+\d+[a-z]?\b/i,
  /\b\d+[a-z]?\s+(?:street|st\.?|avenue|ave\.?|road|rd\.?|boulevard|blvd\.?|close|drive|dr\.?|lane|ln\.?|crescent|cres\.?|way)\b/i,
  /\b\d+\s+[A-Za-z]+\s+(?:street|st\.?|avenue|ave\.?|road|rd\.?|drive|dr\.?|lane|ln\.?|close|way)\b/i,
  /\b(?:street|st\.?|avenue|ave\.?|road|rd\.?|close|drive|dr\.?|lane|ln\.?)\s+(?:no\.?|number)?\s*\d+[a-z]?\b/i,
  /\b\d+\s+[A-Za-z][A-Za-z\s]{0,30}\s+estate\b/i,
  /\b(?:lat(?:itude)?|long(?:itude)?|gps)\s*[:=]?\s*-?\d+\.?\d*/i,
  /\b-?\d{1,3}\.\d{4,}\s*,\s*-?\d{1,3}\.\d{4,}\b/,
];

function containsNigerianPhone(text: string): boolean {
  const candidates = text.match(PHONE_CANDIDATE_PATTERN);
  if (!candidates) return false;

  for (const candidate of candidates) {
    const digits = candidate.replace(/\D/g, "");
    if (digits.length === 11 && digits.startsWith("0")) return true;
    if (digits.length === 13 && digits.startsWith("234")) return true;
    if (digits.length === 10 && /\+?\s*234/.test(candidate)) return true;
  }
  return false;
}

function matchPatterns(text: string, patterns: RegExp[]): boolean {
  return patterns.some((pattern) => pattern.test(text));
}

function containsStreetAddress(text: string): boolean {
  return matchPatterns(text, ADDRESS_PATTERNS);
}

/**
 * Shared contact / link / spam gate — one owner for listing free-text **and**
 * item-request notes. Does **not** include listing-only rules (negotiation, address).
 */
export function scanSharedFreeTextContact(
  text: string,
  options?: { audience?: FreeTextMessageAudience },
): ListingFreeTextViolation[] {
  const audience = options?.audience ?? "listing";
  const trimmed = text.trim();
  if (!trimmed) return [];

  const violations: ListingFreeTextViolation[] = [];
  const seen = new Set<ListingFreeTextViolationCode>();

  const add = (code: ListingFreeTextViolationCode) => {
    if (seen.has(code)) return;
    seen.add(code);
    violations.push({
      code,
      message: freeTextViolationMessage(code, audience),
    });
  };

  if (containsNigerianPhone(trimmed)) {
    add("phone");
  }

  if (EMAIL_PATTERN.test(trimmed)) {
    add("email");
  }

  if (URL_PATTERN.test(trimmed)) {
    add("url");
  }

  if (matchPatterns(trimmed, OFF_PLATFORM_CONTACT_PATTERNS)) {
    add("off_platform_contact");
  }

  return violations;
}

/** First shared-contact violation message, if any. */
export function validateSharedFreeTextContactField(
  text: string,
  options?: { audience?: FreeTextMessageAudience },
): string | undefined {
  return scanSharedFreeTextContact(text, options)[0]?.message;
}

function scanCoreListingFreeText(text: string): ListingFreeTextViolation[] {
  const violations = scanSharedFreeTextContact(text, { audience: "listing" });
  const trimmed = text.trim();
  if (!trimmed) return violations;

  if (matchPatterns(trimmed, NEGOTIATION_PATTERNS)) {
    const seen = new Set(violations.map((v) => v.code));
    if (!seen.has("negotiation")) {
      violations.push({
        code: "negotiation",
        message: freeTextViolationMessage("negotiation", "listing"),
      });
    }
  }

  return violations;
}

/**
 * Returns all distinct violations found in a single free-text field.
 * Empty/whitespace input returns no violations.
 * Address heuristics apply to description/defects only (not title).
 */
export function scanListingFreeText(
  text: string,
  options?: { field?: ListingFreeTextFieldKind },
): ListingFreeTextViolation[] {
  const field = options?.field ?? "description";
  const violations = scanCoreListingFreeText(text);

  if (field !== "title" && text.trim() && containsStreetAddress(text)) {
    const seen = new Set(violations.map((v) => v.code));
    if (!seen.has("address")) {
      violations.push({
        code: "address",
        message: freeTextViolationMessage("address", "listing"),
      });
    }
  }

  return violations;
}

/** Title: Phase 1 high-confidence rules only (no address heuristics). */
export function validateListingTitleField(text: string): string | undefined {
  return scanListingFreeText(text, { field: "title" })[0]?.message;
}

/** Description/defects: Phase 1 rules + address heuristics. */
export function validateListingFreeTextField(text: string): string | undefined {
  return scanListingFreeText(text, { field: "description" })[0]?.message;
}

export function validateListingDescriptionAndDefects(
  description: string,
  defects: string,
): { description?: string; defects?: string } {
  const out: { description?: string; defects?: string } = {};
  const descriptionError = validateListingFreeTextField(description);
  const defectsError = validateListingFreeTextField(defects);
  if (descriptionError) out.description = descriptionError;
  if (defectsError) out.defects = defectsError;
  return out;
}

export function assertListingFreeTextAllowed(
  title: string,
  description: string,
  defects: string,
):
  | { ok: true }
  | {
      ok: false;
      field: ListingFreeTextFieldKind;
      message: string;
    } {
  const titleError = validateListingTitleField(title);
  if (titleError) {
    return { ok: false, field: "title", message: titleError };
  }

  const descriptionError = validateListingFreeTextField(description);
  if (descriptionError) {
    return { ok: false, field: "description", message: descriptionError };
  }

  const defectsError = validateListingFreeTextField(defects);
  if (defectsError) {
    return { ok: false, field: "defects", message: defectsError };
  }

  return { ok: true };
}

/** Full submission scan — all fields with stable violation codes for lifecycle gates. */
export function scanListingSubmissionFreeText(
  title: string,
  description: string,
  defects: string,
): ListingFreeTextViolationWithField[] {
  const withField = (
    field: ListingFreeTextFieldKind,
    text: string,
  ): ListingFreeTextViolationWithField[] =>
    scanListingFreeText(text, { field }).map((violation) => ({
      ...violation,
      field,
    }));

  return [
    ...withField("title", title),
    ...withField("description", description),
    ...withField("defects", defects),
  ];
}
